import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import React, {ReactNode, useRef, useState} from 'react';

import {DRAGGABLE_ANTIBODY_SIZE} from 'antibodies/antibody-map/AntibodyMapConstants';
import {clampAntibodyPreviewCoordinatesToBoundingBox} from 'antibodies/antibody-map/AntibodyMapUtils';
import useAntibodyMapState from 'antibodies/antibody-map/useAntibodyMapState';
import {ZINDEX_DND_CANVAS_DRAGGED_BLOCK} from 'styles/StyleConstants';

export interface AntibodyDragWrapperProps {
  antibodyId: string;
  children: ReactNode;
  domainPreviewRef: React.RefObject<HTMLDivElement>;
}

export default function AntibodyDragWrapper({
  antibodyId,
  children,
  domainPreviewRef,
}: AntibodyDragWrapperProps): JSX.Element {
  const {
    state: {
      dragState: {x, y, offsetX, offsetY, isDragging},
      expandPreview,
    },
    startDrag,
    endDrag,
  } = useAntibodyMapState(antibodyId);
  // When using the HTML5 drag-and-drop API, the dragged element is cloned into a 'ghost element', not
  // represented in the DOM, that follows the cursor. This occurs at the end of the `dragStart` event. The
  // original element remains in the DOM. We want to hide the original element, but only after it is cloned.
  // makeUseGlobalClientState is synchronous, so in order to guarantee that we only hide after the end of the
  // `dragStart` event, we set a second isDragging flag in the `drag` event, which will only fire after the
  // `dragStart` event ends, and condition the hide on _this_ flag.
  const [localIsDragging, setLocalIsDragging] = useState(false);
  const antibodyWrapperRef = useRef<HTMLDivElement>(null);

  if (!expandPreview) {
    return <>{children}</>;
  }

  return (
    <div
      className={css(expandPreview ? styles.antibodyWrapperExpandedPreview : styles.antibodyWrapper)}
      draggable={expandPreview}
      ref={antibodyWrapperRef}
      style={{
        cursor: isDragging ? 'grabbing' : expandPreview ? 'move' : 'auto',
        display: localIsDragging ? 'none' : 'block',
        left: x,
        top: y,
      }}
      onDragStart={(e) => {
        if (expandPreview && antibodyWrapperRef.current != null) {
          const offsetX = e.clientX - antibodyWrapperRef.current.offsetLeft;
          const offsetY = e.clientY - antibodyWrapperRef.current.offsetTop;
          startDrag(offsetX, offsetY);
        }
      }}
      onDrag={() => {
        if (isDragging && !localIsDragging) {
          setLocalIsDragging(true);
        }
      }}
      onDragEnd={(e) => {
        if (e.dataTransfer.dropEffect === 'none') {
          // If dropped out-of-bounds, go back to the original location.
          endDrag(x, y);
        } else if (domainPreviewRef.current != null) {
          const rawX = e.clientX - offsetX;
          const rawY = e.clientY - offsetY;
          const [newX, newY] = clampAntibodyPreviewCoordinatesToBoundingBox(
            domainPreviewRef.current,
            rawX,
            rawY
          );
          endDrag(newX, newY);
        }
        setLocalIsDragging(false);
      }}
    >
      {children}
    </div>
  );
}

const styles = StyleSheet.create({
  antibodyWrapper: {
    flex: '1 1 auto',
    minHeight: 0,
  },
  antibodyWrapperExpandedPreview: {
    position: 'absolute',
    zIndex: ZINDEX_DND_CANVAS_DRAGGED_BLOCK,
    width: `${DRAGGABLE_ANTIBODY_SIZE}px`,
    height: `${DRAGGABLE_ANTIBODY_SIZE}px`,
    padding: '4px',
    backgroundColor: Color.WHITE,
    border: `1px solid ${Color.BLUE_350}`,
    borderRadius: '4px',
    touchAction: 'none',
  },
});
